import { Link } from 'react-router-dom'
import { PageHeader } from '@/design-system/page-header'
import { Card, CardContent } from '@/design-system/card'
import { segments } from '@/consultant-study/segments'
import { microprofiles } from '@/consultant-study/microprofiles'
import { ProfileCard } from '@/segments/profile-card'
import { ArrowRight } from 'lucide-react'

const segmentGuidance: Record<string, {
  action: string
  actionColor: string
  bullets: string[]
}> = {
  S1: {
    action: 'Protect',
    actionColor: '#16a34a',
    bullets: [
      'Give them your highest-value, most complex leads',
      'Use as mentors for S4 and S5 — don\'t over-train them',
      'Keep engaged with variety and ownership, not more process',
    ],
  },
  S2: {
    action: 'Grow',
    actionColor: '#2563EB',
    bullets: [
      'Route affluent, benefit-conscious clients their way',
      'Help them build deeper, longer-term client relationships',
      'Don\'t assign price-shoppers — it wastes their value-selling strength',
    ],
  },
  S3: {
    action: 'Stabilize',
    actionColor: '#DC2626',
    bullets: [
      'Reduce workload first — before any new training',
      'Assign stable renewals and repeat clients, not new campaigns',
      'Watch for burnout: declining response times, missed follow-ups',
    ],
  },
  S4: {
    action: 'Rebuild',
    actionColor: '#6B7280',
    bullets: [
      'This is an identity gap, not a skills gap — mentorship over training',
      'Start with easy wins: simple inquiries, warm referrals',
      'Largest segment (26.2%) — biggest development opportunity in the network',
    ],
  },
  S5: {
    action: 'Activate',
    actionColor: '#8B5CF6',
    bullets: [
      'Give structured lead batches with clear deadlines — they need the push',
      'Pair with S1 mentors to build a proactive sales rhythm',
      'Solid foundation (zero stress, high trust) — just needs the right signal',
    ],
  },
}

const maxConversion = Math.max(...microprofiles.map(m => m.conversionRate))
const maxLeads = Math.max(...microprofiles.map(m => m.leadsPerMonth))

export default function ProfilePlaybookPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">
      <PageHeader
        title="Microprofiles"
        subtitle="12 behavioral archetypes across 5 segments — each with its own playbook"
      />

      {/* Narrative introduction */}
      <section>
        <Card className="border-l-4 border-l-[#F17922] bg-[#F17922]/[0.02]">
          <CardContent className="py-6">
            <p className="text-sm leading-relaxed text-gray-700">
              Segments tell you <em>who</em> your consultants are. Microprofiles tell you <em>what to do</em> about
              it. Within each segment, consultants differ in closing style, lead preference, and coaching
              needs. Matching the right lead to the right microprofile is the fastest lever for improving
              conversion without changing lead volume.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Profile cards grouped by segment */}
      {segments.map((segment) => {
        const profiles = microprofiles.filter((m) => m.segmentId === segment.id)
        if (profiles.length === 0) return null

        return (
          <section key={segment.id}>
            <div className="mb-2 flex items-center gap-3">
              <div
                className="w-3 h-3"
                style={{ backgroundColor: segment.color }}
              />
              <h2 className="text-xl font-bold text-gray-900">{segment.nickname}</h2>
              <span className="text-sm text-gray-400">
                {segment.name} &middot; N={segment.n}
              </span>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              {segment.description}
            </p>

            {/* Segment guidance */}
            {(() => {
              const g = segmentGuidance[segment.id]
              if (!g) return null
              return (
                <div
                  className="mb-6 flex items-start gap-4 border px-5 py-4"
                  style={{ borderColor: `${segment.color}30`, backgroundColor: `${segment.color}04` }}
                >
                  <span
                    className="shrink-0 mt-0.5 text-sm font-black uppercase tracking-wider"
                    style={{ color: g.actionColor }}
                  >
                    {g.action}
                  </span>
                  <ul className="space-y-1.5">
                    {g.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
                        <span
                          className="mt-1.5 w-1.5 h-1.5 shrink-0"
                          style={{ backgroundColor: segment.color }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })()}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((profile) => (
                <ProfileCard key={profile.id} profile={profile} />
              ))}
            </div>
          </section>
        )
      })}

      {/* Performance comparison */}
      <section>
        <h2 className="text-lg font-semibold mb-2">Performance at a Glance</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Conversion rate and lead volume side by side. The leads bar shows converted (solid)
          vs. total (light).
        </p>
        <Card>
          <CardContent className="py-4">
            <div className="space-y-1.5">
              {/* Header */}
              <div className="grid grid-cols-[140px_1fr_1fr] gap-3 px-2 pb-2 border-b border-gray-200">
                <div />
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Conversion Rate
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  Converted / Total Leads per Month
                </div>
              </div>
              {/* Rows */}
              {microprofiles.map(mp => {
                const seg = segments.find(s => s.id === mp.segmentId)
                const color = seg?.color ?? '#6B7280'
                const convPct = (mp.conversionRate / maxConversion) * 100
                const leadPct = (mp.leadsPerMonth / maxLeads) * 100
                const converted = mp.leadsPerMonth * mp.conversionRate / 100
                const convertedPct = (converted / mp.leadsPerMonth) * 100
                return (
                  <div
                    key={mp.id}
                    className="grid grid-cols-[140px_1fr_1fr] gap-3 items-center px-2 py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    {/* Label */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-bold text-white shrink-0"
                        style={{ backgroundColor: color }}
                      >
                        {mp.id}
                      </span>
                      <span className="text-xs font-medium text-gray-700 truncate">{mp.nickname}</span>
                    </div>
                    {/* Conversion bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-5 bg-gray-100 overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${convPct}%`, backgroundColor: color }}
                        />
                      </div>
                      <span className="text-xs font-mono text-gray-700 w-12 text-right shrink-0">
                        {mp.conversionRate}%
                      </span>
                    </div>
                    {/* Leads bar with conversion marker */}
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 h-5 bg-gray-100 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0"
                          style={{ width: `${leadPct}%`, backgroundColor: `${color}20` }}
                        />
                        <div
                          className="absolute inset-y-0 left-0"
                          style={{ width: `${leadPct * convertedPct / 100}%`, backgroundColor: color }}
                        />
                        <div
                          className="absolute top-0 bottom-0 w-px"
                          style={{
                            left: `${leadPct * convertedPct / 100}%`,
                            backgroundColor: '#1f2937',
                          }}
                        />
                      </div>
                      <span className="text-xs font-mono text-gray-700 w-20 text-right shrink-0">
                        <span style={{ color }}>{converted.toFixed(1)}</span>
                        <span className="text-gray-400"> / {mp.leadsPerMonth}</span>
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Key takeaway */}
      <section>
        <Card className="border-l-4 border-l-[#F17922] bg-[#F17922]/[0.02]">
          <CardContent className="py-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">The Takeaway</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              Every segment requires a different management strategy. Stars need protection.
              The Disconnected need identity restoration, not skills training. Pressured Veterans
              need workload relief before anything else. One-size-fits-all is the enemy of performance.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* CTA to matching */}
      <section className="text-center py-8">
        <Card className="border-[#F17922]/30 bg-[#F17922]/[0.02] inline-block">
          <CardContent className="py-6 px-8">
            <p className="text-sm text-gray-600 mb-3">
              Ready to match these profiles to leads?
            </p>
            <Link
              to="/matching"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#F17922] hover:underline"
            >
              Explore the Matching Vision <ArrowRight className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
